import { useMemo, useState } from "react";
import {
  useGetSalesQuery,
  useGetCustomersQuery,
  useGetWarehousesQuery,
  useDeleteSaleMutation,
} from "../../../../redux/api";
import type { Sale } from "../../../../redux/api";
import {
  IconPlus,
  IconSearch,
  IconEye,
  IconEdit,
  IconTrash,
} from "../../../../components/icons";
import { SaleFormModal } from "./sale";
import { EntityOverview } from "../../../../components/EntityOverview";
import { OverviewFieldList } from "../../../../components/OverviewFieldList";
import { statusTone as overviewStatusTone } from "../../../../utils/status";
import { RowMenu } from "../../../../components/RowMenu";

const COLUMN_COUNT = 6;

const STATUS_TONE: Record<string, string> = {
  draft: "badge--info",
  confirmed: "badge--warning",
  shipped: "badge--info",
  delivered: "badge--success",
  cancelled: "badge--danger",
};

const statusTone = (status?: string | null) =>
  STATUS_TONE[(status ?? "").toLowerCase()] ?? "badge--info";

const formatDate = (iso?: string | null) =>
  iso ? new Date(iso).toLocaleDateString() : "—";

// Inventory → Sales (list page).
const Sales = () => {
  const {
    data: sales = [],
    isLoading,
    isFetching,
    error,
    refetch,
  } = useGetSalesQuery();

  const { data: customers = [] } = useGetCustomersQuery();
  const { data: warehouses = [] } = useGetWarehousesQuery();

  const [deleteSale] = useDeleteSaleMutation();

  const [search, setSearch] = useState("");

  const customerName = (id?: string | null) =>
    id ? customers.find((c) => c.id === id)?.name ?? id : "—";

  const warehouseName = (id?: string | null) =>
    id ? warehouses.find((w) => w.id === id)?.name ?? id : "—";

  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const openDetail = (id: string | null) => {
    setEditingId(id);
    setModalOpen(true);
  };

  const [viewingId, setViewingId] = useState<string | null>(null);
  const viewing = sales.find((s) => s.id === viewingId) ?? null;


  const handleDelete = async (sale: Sale) => {
    if (!window.confirm(`Delete sale "${sale.saleNumber}"?`)) return;
    try {
      await deleteSale(sale.id).unwrap();
    } catch (err) {
      window.alert(
        (err as { message?: string })?.message ?? "Failed to delete sale.",
      );
    }
  };

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return sales;
    return sales.filter((s: Sale) =>
      [s.saleNumber, s.status, customerName(s.customerId)].some((field) =>
        (field ?? "").toLowerCase().includes(q),
      ),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sales, search, customers]);

  const errorMessage = error
    ? (error as { message?: string }).message ?? "Failed to load sales"
    : null;

  if (viewing) {
    return (
      <>
        <EntityOverview
          backLabel="Sales"
          onBack={() => setViewingId(null)}
          title={viewing.saleNumber ?? viewing.id}
          id={viewing.id}
          status={{ label: viewing.status, tone: overviewStatusTone(viewing.status) }}
          onEdit={() => openDetail(viewing.id)}
          onDelete={() => {
            void handleDelete(viewing).then(() => setViewingId(null));
          }}
        >
          <OverviewFieldList
            fields={[
              { label: "Sale date", value: formatDate(viewing.saleDate) },
              { label: "Customer", value: customerName(viewing.customerId) },
              { label: "Warehouse", value: warehouseName(viewing.warehouseId) },
              { label: "Tax %", value: viewing.taxPercentage ?? 0 },
              { label: "Notes", value: viewing.notes, full: true },
            ]}
          />

          <h3 className="panel__section-title">Line items</h3>
          <div className="table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  <th style={{ width: "40%" }}>Product</th>
                  <th>Qty</th>
                  <th>Unit price</th>
                  <th>Line total</th>
                </tr>
              </thead>
              <tbody>
                {viewing.items.length === 0 ? (
                  <tr>
                    <td className="data-table__empty" colSpan={4}>
                      No items.
                    </td>
                  </tr>
                ) : (
                  viewing.items.map((row, index) => (
                    <tr key={index}>
                      <td>{row.productName || row.productId}</td>
                      <td>{row.quantity}</td>
                      <td>{row.unitPrice.toLocaleString()}</td>
                      <td>{row.lineTotal.toLocaleString()}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <div className="totals">
            <div>
              <span>Subtotal</span>
              <strong>{viewing.subtotal.toLocaleString()}</strong>
            </div>
            <div>
              <span>Tax ({viewing.taxPercentage ?? 0}%)</span>
              <strong>{viewing.taxAmount.toLocaleString()}</strong>
            </div>
            <div className="totals__grand">
              <span>Total</span>
              <strong>{viewing.total.toLocaleString()}</strong>
            </div>
          </div>
        </EntityOverview>
        <SaleFormModal open={modalOpen} id={editingId} onClose={() => setModalOpen(false)} />
      </>
    );
  }

  return (
    <div className="dashboard">
      <section className="panel">
        <div className="panel__head">
          <div>
            <h2>Sales</h2>
            <p className="panel__sub">
              Showing {visible.length} of {sales.length}
            </p>
          </div>
          <div className="toolbar">
            <div className="search">
              <span className="search__icon">
                <IconSearch />
              </span>
              <input
                type="search"
                placeholder="Search sales…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <button
              type="button"
              className="btn"
              onClick={() => refetch()}
              disabled={isFetching}
            >
              {isFetching ? "Refreshing…" : "Refresh"}
            </button>
            <button
              type="button"
              className="btn btn--primary"
              onClick={() => openDetail(null)}
            >
              <IconPlus /> New sale
            </button>
          </div>
        </div>

        <div className="table-wrap">
          <table className="data-table">
            <thead>
              <tr>
                <th>Sale #</th>
                <th>Customer</th>
                <th>Date</th>
                <th>Total</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td className="data-table__empty" colSpan={COLUMN_COUNT}>
                    Loading sales…
                  </td>
                </tr>
              ) : errorMessage ? (
                <tr>
                  <td className="data-table__empty" colSpan={COLUMN_COUNT}>
                    <div className="table-error">
                      <span>{errorMessage}</span>
                      <button
                        type="button"
                        className="btn btn--ghost"
                        onClick={() => refetch()}
                      >
                        Try again
                      </button>
                    </div>
                  </td>
                </tr>
              ) : visible.length === 0 ? (
                <tr>
                  <td className="data-table__empty" colSpan={COLUMN_COUNT}>
                    {sales.length === 0
                      ? "No sales yet."
                      : "No sales match your search."}
                  </td>
                </tr>
              ) : (
                visible.map((sale: Sale) => (
                  <tr key={sale.id}>
                    <td className="cell-title">
                      <button
                        type="button"
                        className="btn btn--ghost"
                        onClick={() => setViewingId(sale.id)}
                      >
                        {sale.saleNumber}
                      </button>
                    </td>
                    <td>{customerName(sale.customerId)}</td>
                    <td className="cell-date">{formatDate(sale.saleDate)}</td>
                    <td className="cell-date">
                      {sale.total?.toLocaleString?.() ?? sale.total}
                    </td>
                    <td>
                      <span className={`badge ${statusTone(sale.status)}`}>
                        <span className="badge__dot" />
                        {sale.status}
                      </span>
                    </td>
                    <td>
                      <RowMenu
                        items={[
                          {
                            label: "View",
                            icon: <IconEye size={16} />,
                            onSelect: () => setViewingId(sale.id),
                          },
                          {
                            label: "Edit",
                            icon: <IconEdit size={16} />,
                            onSelect: () => openDetail(sale.id),
                          },
                          {
                            label: "Delete",
                            icon: <IconTrash size={16} />,
                            onSelect: () => handleDelete(sale),
                            danger: true,
                          },
                        ]}
                      />
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>


      <SaleFormModal open={modalOpen} id={editingId} onClose={() => setModalOpen(false)} />
    </div>
  );
};

export default Sales;
